import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Plus, Edit, Calendar, MapPin, CheckCircle, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import ExperienceForm from '@/components/dashboard/experience-form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ExperienceSectionProps {
  isEditing: boolean;
}

export function ExperienceSection({ isEditing }: ExperienceSectionProps) {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [experiences, setExperiences] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState<any>(null);

  useEffect(() => {
    if (id) {
      fetchExperiences(id);
    }
  }, [id]);

  const fetchExperiences = async (profileId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('experiences')
        .select('*')
        .eq('profile_id', profileId);

      if (error) {
        console.error('Error fetching experiences:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to load experience data',
        });
      } else {
        // Sort experiences: current positions first, then by start date descending
        const sortedExperiences = data?.sort((a, b) => {
          // If one is current and the other isn't, current comes first
          if (a.current && !b.current) return -1;
          if (!a.current && b.current) return 1;
          
          // If both are current or both are not current, sort by start date
          const aDate = new Date(a.start_date).getTime();
          const bDate = new Date(b.start_date).getTime();
          return bDate - aDate;
        }) || [];
        
        setExperiences(sortedExperiences);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (exp: any) => {
    setSelectedExperience(exp);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedExperience) return;
    
    try {
      const { error } = await supabase
        .from('experiences')
        .delete()
        .eq('id', selectedExperience.id);

      if (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message || 'Failed to delete experience',
        });
      } else {
        toast({
          title: 'Success',
          description: 'Experience deleted successfully',
        });
        if (id) {
          fetchExperiences(id);
        }
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedExperience(null);
    }
  };

  const confirmDelete = (exp: any) => {
    setSelectedExperience(exp);
    setDeleteDialogOpen(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Present';
    return format(new Date(dateString), 'MMM yyyy');
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (experiences.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No work experience added yet.</p>
        {isEditing && (
          <p className="text-sm text-muted-foreground mt-1">
            Click the Add button to add your first work experience.
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {experiences.map((exp) => (
          <Card key={exp.id} className="group hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium">{exp.position}</h3>
                  <p className="text-sm text-muted-foreground">{exp.company_name}</p>
                  <div className="flex items-center gap-2 mt-1 text-sm">
                    <span>
                      {formatDate(exp.start_date)} - {exp.current ? 'Present' : formatDate(exp.end_date)}
                    </span>
                    {exp.location && (
                      <>
                        <span>•</span>
                        <span>{exp.location}</span>
                      </>
                    )}
                  </div>
                </div>
                {isEditing && (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(exp)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => confirmDelete(exp)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {exp.description && (
                <>
                  <Separator className="my-3" />
                  <div className="whitespace-pre-line">
                    {exp.description}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <ExperienceForm
        open={formOpen}
        onOpenChange={setFormOpen}
        experience={selectedExperience}
        onSuccess={() => {
          fetchExperiences(id!);
          setSelectedExperience(null);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this work experience.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}